import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import api from "../../utils/api";

function injectScriptOnce(id, src) {
    return new Promise((resolve, reject) => {
        if (document.getElementById(id)) {
            resolve();
            return;
        }

        const script = document.createElement("script");
        script.id = id;
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load " + src));
        document.head.appendChild(script);
    });
}

function injectStylesOnce(id, href) {
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
}

export default function ClassroomScheduler() {
    const calendarElRef = useRef(null);
    const calendarInstanceRef = useRef(null);
    const viewRangeRef = useRef({ start: null, end: null });

    const [loading, setLoading] = useState(true);
    const [classrooms, setClassrooms] = useState([]);
    const [teachers, setTeachers] = useState([]);

    const [selectedClassroomId, setSelectedClassroomId] = useState("");
    const selectedClassroom = useMemo(() => classrooms.find((c) => String(c.id) === String(selectedClassroomId)) || null, [
        classrooms,
        selectedClassroomId,
    ]);

    const [classroomName, setClassroomName] = useState("");

    const selectedTeacherId = useMemo(() => {
        return selectedClassroom?.assigned_teacher_id ?? "";
    }, [selectedClassroom]);

    const [mode, setMode] = useState("busy"); // "busy" | "lesson"

    const [busyModalOpen, setBusyModalOpen] = useState(false);
    const [lessonModalOpen, setLessonModalOpen] = useState(false);

    const [selectInfo, setSelectInfo] = useState({ start: null, end: null });
    const [busyReason, setBusyReason] = useState("");

    const [meetingType, setMeetingType] = useState("agora");
    const [meetingLink, setMeetingLink] = useState("");

    const refreshEvents = async () => {
        const calendar = calendarInstanceRef.current;
        if (!calendar) return;
        if (!selectedClassroomId) return;

        const range = viewRangeRef.current;
        if (!range.start || !range.end) return;

        const res = await api.get(`admin/classrooms/${selectedClassroomId}/calendar`, {
            params: { start: range.start, end: range.end },
        });

        const payload = res.data?.data || {};
        const busyEvents = payload.busyEvents || [];
        const lessonEvents = payload.lessonEvents || [];

        calendar.removeAllEvents();

        busyEvents.forEach((e) => {
            calendar.addEvent({
                ...e,
                backgroundColor: "#dc3545",
                borderColor: "#dc3545",
                textColor: "#fff",
            });
        });

        lessonEvents.forEach((e) => {
            calendar.addEvent({
                ...e,
                backgroundColor: "#198754",
                borderColor: "#198754",
                textColor: "#fff",
            });
        });
    };

    const openBusyModal = (info) => {
        setBusyReason("");
        setSelectInfo({ start: info.startStr, end: info.endStr });
        setBusyModalOpen(true);
    };

    const openLessonModal = (info) => {
        setMeetingType("agora");
        setMeetingLink("");
        setSelectInfo({ start: info.startStr, end: info.endStr });
        setLessonModalOpen(true);
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                injectStylesOnce("fc-css", "https://cdn.jsdelivr.net/npm/fullcalendar@6.1.11/index.global.min.css");
                await injectScriptOnce("fc-js", "https://cdn.jsdelivr.net/npm/fullcalendar@6.1.11/index.global.min.js");

                // classrooms = purchased courses = "classrooms" in this scheduler.
                const classroomsRes = await api.get("admin/purchased-courses", {
                    params: { page: 1, perPage: 500 },
                });
                if (!mounted) return;
                setClassrooms(classroomsRes.data?.data || []);

                const teachersRes = await api.get("admin/teachers", { params: { page: 1, perPage: 500 } });
                if (!mounted) return;
                setTeachers(teachersRes.data?.data || []);

                // pick first classroom
                const first = (classroomsRes.data?.data || [])[0];
                if (first) setSelectedClassroomId(first.id);
            } catch (e) {
                console.error("Scheduler init failed", e);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!calendarElRef.current) return;
        if (calendarInstanceRef.current) return;
        if (typeof window.FullCalendar === "undefined") return;

        const calendarEl = calendarElRef.current;
        const calendar = new window.FullCalendar.Calendar(calendarEl, {
            initialView: "timeGridWeek",
            height: "auto",
            selectable: true,
            selectMirror: true,
            slotMinTime: "06:00:00",
            slotMaxTime: "22:00:00",
            headerToolbar: {
                left: "prev,next today",
                center: "title",
                right: "timeGridWeek,timeGridDay",
            },
            datesSet: function (arg) {
                // Keep latest range and refresh events.
                viewRangeRef.current = { start: arg.startStr, end: arg.endStr };
                refreshEvents();
            },
            select: function (info) {
                if (!selectedClassroomId) return;
                if (mode === "busy") {
                    openBusyModal(info);
                } else if (mode === "lesson") {
                    openLessonModal(info);
                }
            },
            eventClick: function (info) {
                const ev = info.event;
                const props = ev.extendedProps || {};
                if (props.status === "busy" && String(ev.id || "").startsWith("busy_")) {
                    const raw = String(ev.id);
                    const id = raw.replace("busy_", "");
                    if (window.confirm("Delete this busy slot?")) {
                        api
                            .delete(`admin/teacher-busy-slots/${id}`)
                            .then(() => refreshEvents())
                            .catch((e) => alert(e?.response?.data?.message || "Failed to delete busy slot."));
                    }
                }
            },
            events: [],
        });

        calendar.render();
        calendarInstanceRef.current = calendar;
    }, [selectedClassroomId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // Update name input on classroom change.
        setClassroomName(selectedClassroom?.classroom_name || "");
    }, [selectedClassroom]);

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <CircularProgress size={24} />
                <Typography sx={{ mt: 1 }}>Loading scheduler…</Typography>
            </Box>
        );
    }

    const handleSaveClassroomName = async () => {
        if (!selectedClassroomId) return;
        try {
            await api.put(`admin/classrooms/${selectedClassroomId}/meta`, { name: classroomName || null });
            // refresh classrooms list so label stays consistent
            const classroomsRes = await api.get("admin/purchased-courses", { params: { page: 1, perPage: 500 } });
            setClassrooms(classroomsRes.data?.data || []);
        } catch (e) {
            alert(e?.response?.data?.message || "Failed to save classroom name.");
        }
    };

    const handleSaveBusy = async () => {
        try {
            const teacherId = selectedTeacherId;
            if (!teacherId) {
                alert("Classroom has no assigned teacher.");
                return;
            }
            await api.post("admin/teacher-busy-slots", {
                teacher_id: teacherId,
                start_at: selectInfo.start,
                end_at: selectInfo.end,
                reason: busyReason || null,
            });
            setBusyModalOpen(false);
            refreshEvents();
        } catch (e) {
            alert(e?.response?.data?.message || "Failed to save busy slot.");
        }
    };

    const handleSaveLesson = async () => {
        try {
            await api.post(`admin/classrooms/${selectedClassroomId}/lessons`, {
                start_at: selectInfo.start,
                end_at: selectInfo.end,
                meeting_type: meetingType,
                meeting_link: meetingLink || null,
            });
            setLessonModalOpen(false);
            refreshEvents();
        } catch (e) {
            alert(e?.response?.data?.message || "Failed to create lesson.");
        }
    };

    const handleDeleteAllLessons = async () => {
        if (!selectedClassroomId) return;
        if (!window.confirm("Delete all lessons for this classroom?")) return;
        try {
            await api.delete(`admin/classrooms/${selectedClassroomId}/lessons`);
            refreshEvents();
        } catch (e) {
            alert(e?.response?.data?.message || "Failed to delete classroom lessons.");
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Classroom Scheduler
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <FormControl sx={{ minWidth: 280 }}>
                    <InputLabel id="classroom-select-label">Classroom</InputLabel>
                    <Select
                        labelId="classroom-select-label"
                        label="Classroom"
                        value={selectedClassroomId}
                        onChange={(e) => setSelectedClassroomId(e.target.value)}
                    >
                        {classrooms.map((c) => {
                            const courseName = c.course_name_en || c.course_name_ru || c.course_name_hy || "Course";
                            const userName = `${c.user_first_name || ""} ${c.user_last_name || ""}`.trim();
                            const label = c.classroom_name ? c.classroom_name : `${courseName} - ${userName}`;
                            return (
                                <MenuItem key={c.id} value={c.id}>
                                    {label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                <TextField
                    label="Classroom name"
                    value={classroomName}
                    onChange={(e) => setClassroomName(e.target.value)}
                    size="small"
                    sx={{ minWidth: 260 }}
                />
                <Button variant="outlined" onClick={handleSaveClassroomName}>
                    Save name
                </Button>

                <FormControl sx={{ minWidth: 280 }}>
                    <InputLabel id="teacher-select-label">Teacher</InputLabel>
                    <Select
                        labelId="teacher-select-label"
                        label="Teacher"
                        value={selectedTeacherId || ""}
                        disabled={!!selectedTeacherId}
                        onChange={(e) => {
                            // Keeping teacher fixed once set (per spec). If you need changing, enable later.
                        }}
                    >
                        <MenuItem value="">
                            No teacher assigned
                        </MenuItem>
                        {teachers.map((t) => {
                            const full = `${t.first_name || ""} ${t.last_name || ""}`.trim();
                            return (
                                <MenuItem key={t.id} value={t.id}>
                                    {full || `Teacher #${t.id}`}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
                <Button variant={mode === "busy" ? "contained" : "outlined"} onClick={() => setMode("busy")}>
                    Mark busy
                </Button>
                <Button variant={mode === "lesson" ? "contained" : "outlined"} onClick={() => setMode("lesson")}>
                    Create lesson
                </Button>
                <Button color="error" variant="outlined" onClick={handleDeleteAllLessons} disabled={!selectedClassroomId}>
                    Delete all lessons
                </Button>
            </Stack>

            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                <div ref={calendarElRef} />
            </Box>

            <Dialog open={busyModalOpen} onClose={() => setBusyModalOpen(false)}>
                <DialogTitle>Save busy slot</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
                        {selectInfo.start} → {selectInfo.end}
                    </Typography>
                    <TextField
                        label="Reason (optional)"
                        value={busyReason}
                        onChange={(e) => setBusyReason(e.target.value)}
                        fullWidth
                        size="small"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBusyModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveBusy}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={lessonModalOpen} onClose={() => setLessonModalOpen(false)}>
                <DialogTitle>Create lesson</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
                        {selectInfo.start} → {selectInfo.end}
                    </Typography>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="meeting-type-label">Meeting type</InputLabel>
                            <Select
                                labelId="meeting-type-label"
                                label="Meeting type"
                                value={meetingType}
                                onChange={(e) => setMeetingType(e.target.value)}
                            >
                                <MenuItem value="agora">Agora</MenuItem>
                                <MenuItem value="google_meet">Google Meet</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Meeting link (optional)"
                            value={meetingLink}
                            onChange={(e) => setMeetingLink(e.target.value)}
                            fullWidth
                            size="small"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLessonModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveLesson}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

